import { useState } from 'react';

interface UpdateItem {
  name: string;
  currentVersion: string;
  newVersion: string;
  changelog: string;
  icon: string;
  critical: boolean;
}

const updatesList: UpdateItem[] = [
  {
    name: 'Evolution API',
    currentVersion: 'v2.2.1',
    newVersion: 'v2.3.0',
    changelog: 'Novo suporte a multi-dispositivo, correção de bugs no webhook, melhoria na fila de mensagens, suporte a áudio em grupo.',
    icon: 'ri-whatsapp-line',
    critical: false,
  },
  {
    name: 'Docker',
    currentVersion: 'v26.1.3',
    newVersion: 'v27.2.0',
    changelog: 'Melhorias de performance, correções de segurança (CVE-2025-xxxxx), novo sistema de build.',
    icon: 'ri-ship-2-line',
    critical: true,
  },
  {
    name: 'Redis',
    currentVersion: 'v7.2.5',
    newVersion: 'v7.4.1',
    changelog: 'Correção de memory leak, otimização de performance em 15%, novo comando ZRANK.',
    icon: 'ri-database-line',
    critical: false,
  },
  {
    name: 'PostgreSQL',
    currentVersion: 'v16.4',
    newVersion: 'v16.6',
    changelog: 'Correções de segurança, melhoria em índices GIN, otimização de vacuum.',
    icon: 'ri-database-2-line',
    critical: true,
  },
  {
    name: 'Ubuntu',
    currentVersion: '24.04 LTS',
    newVersion: '24.04.2 LTS',
    changelog: 'Atualizações de kernel, patches de segurança, drivers atualizados.',
    icon: 'ri-ubuntu-line',
    critical: false,
  },
  {
    name: 'Nginx',
    currentVersion: 'v1.26.1',
    newVersion: 'v1.26.3',
    changelog: 'Correção de vulnerabilidade HTTP/3, melhoria na compressão gzip.',
    icon: 'ri-globe-line',
    critical: false,
  },
  {
    name: 'Certbot',
    currentVersion: 'v2.11.0',
    newVersion: 'v3.1.0',
    changelog: 'Novo suporte a ACME v2, renovação automática melhorada, suporte a wildcard.',
    icon: 'ri-shield-check-line',
    critical: false,
  },
];

export default function SettingsUpdatesPage() {
  const [updating, setUpdating] = useState<string | null>(null);
  const [ignored, setIgnored] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  const handleUpdate = async (name: string) => {
    setUpdating(name);
    setMessage(null);
    // Simulação - na prática chamaria uma Edge Function
    await new Promise((r) => setTimeout(r, 2000));
    setUpdating(null);
    setMessage({ type: 'success', text: `${name} atualizado com sucesso!` });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleIgnore = (name: string) => {
    setIgnored((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
  };

  const visibleUpdates = updatesList.filter((u) => !ignored.has(u.name));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">Atualizações</h2>
        <p className="text-sm text-foreground-500 mt-1">Verifique e aplique atualizações do sistema manualmente</p>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-alert-line text-xl text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-800 mb-1">Nunca atualizamos automaticamente</p>
          <p className="text-xs text-amber-700 leading-relaxed">
            Todas as atualizações precisam ser aprovadas e executadas manualmente por você. 
            Isso evita indisponibilidade por incompatibilidades ou bugs em novas versões.
            Recomendamos testar em ambiente de staging antes de aplicar em produção.
          </p>
        </div>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : message.type === 'warning' ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <i className={`text-lg ${message.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-500'}`} />
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      {visibleUpdates.length === 0 ? (
        <div className="bg-background-100 rounded-2xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-double-line text-3xl text-emerald-500" />
          </div>
          <p className="text-sm font-semibold text-foreground-900 mb-1">Tudo atualizado!</p>
          <p className="text-xs text-foreground-500">Nenhuma atualização pendente no momento.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleUpdates.map((item) => (
            <div key={item.name} className={`bg-background-100 rounded-2xl border p-5 transition-all ${item.critical ? 'border-red-200 bg-red-50/30' : 'border-background-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${item.critical ? 'bg-red-100' : 'bg-background-100'}`}>
                    <i className={`${item.icon} text-lg ${item.critical ? 'text-red-600' : 'text-foreground-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-foreground-900">{item.name}</h3>
                      {item.critical && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-md uppercase">
                          Segurança
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-foreground-500">Versão Atual:</span>
                      <code className="text-xs bg-background-100 px-2 py-0.5 rounded font-mono text-foreground-600">{item.currentVersion}</code>
                      <i className="ri-arrow-right-line text-foreground-400 text-xs" />
                      <span className="text-xs text-foreground-500">Nova:</span>
                      <code className="text-xs bg-primary-50 px-2 py-0.5 rounded font-mono text-primary-700 font-semibold">{item.newVersion}</code>
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed max-w-2xl">{item.changelog}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleUpdate(item.name)}
                    disabled={updating !== null}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    {updating === item.name ? (
                      <><i className="ri-loader-4-line animate-spin" /> Atualizando...</>
                    ) : (
                      <><i className="ri-upload-line" /> Atualizar</>
                    )}
                  </button>
                  <button
                    onClick={() => handleIgnore(item.name)}
                    className="px-4 py-2 text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                  >
                    <i className="ri-close-line" /> Ignorar
                  </button>
                  <button className="px-4 py-2 text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                    <i className="ri-file-list-3-line" /> Ver Mudanças
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {ignored.size > 0 && (
        <div className="bg-background-100 rounded-2xl border border-background-200 p-5">
          <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-3">Atualizações Ignoradas</h3>
          <div className="flex flex-wrap gap-2">
            {Array.from(ignored).map((name) => (
              <span key={name} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-background-100 rounded-lg text-xs text-foreground-500">
                {name}
                <button
                  onClick={() => setIgnored((prev) => { const n = new Set(prev); n.delete(name); return n; })}
                  className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-line text-xs" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}